import React, { useCallback, useEffect, useState } from "react";
import {
    FeedComposer,
    FeedComposerButton,
    FeedComposerInput,
    FeedDetailComposerDock,
    FeedDetailSection,
    FeedInlineState,
    FeedModalList,
    FeedRetryButton,
    FeedSectionLabel,
    FeedTextButton
} from "src/components/NoticeboardFeedUi";
import { StandardModalFixedLayout, StandardModalScrollArea } from "src/components";
import { createNoticeComment, getNoticeComments } from "../../api/feedApi";
import { getReplyPlaceholder, sanitizeNoticeboardComment } from "../../feedUtils";
import { NoticeboardComment } from "./NoticeboardFeedComments";

const NoticeboardCommentsPanel = ({
    token,
    noticeUuid,
    interactionUuid,
    allowComments,
    showDiscussion,
    onCommentCreated,
    postContent
}) => {
    const [comments, setComments] = useState([]);
    const [page, setPage] = useState(1);
    const [nextPageUrl, setNextPageUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [commentText, setCommentText] = useState("");
    const [replyingTo, setReplyingTo] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(false);

    const loadComments = useCallback(async (requestedPage) => {
        setLoading(true);
        setError(false);
        try {
            const response = await getNoticeComments(token, interactionUuid, requestedPage);
            setComments(current => requestedPage === 1 ? response.data : [...current, ...response.data]);
            setNextPageUrl(response.nextPageUrl);
            setPage(requestedPage);
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [interactionUuid, token]);

    useEffect(() => {
        setComments([]);
        setPage(1);
        setNextPageUrl(null);
        setReplyingTo(null);
        setCommentText("");
        setLoading(false);
        setError(false);
        if (showDiscussion) {
            loadComments(1);
        }
    }, [loadComments, showDiscussion]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        const sanitizedComment = sanitizeNoticeboardComment(commentText).trim();
        if (!sanitizedComment || submitting) {
            return;
        }

        const target = interactionUuid === noticeUuid ?
            { noticeUuid } :
            { publicationUuid: interactionUuid };
        setSubmitting(true);
        setSubmitError(false);
        try {
            await createNoticeComment(
                token,
                target,
                sanitizedComment,
                replyingTo ? replyingTo.uuid : null
            );
            setCommentText("");
            setReplyingTo(null);
            await loadComments(1);
            onCommentCreated();
        } catch {
            setSubmitError(true);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <StandardModalFixedLayout>
            <StandardModalScrollArea padding="0">
                {postContent}
                {showDiscussion && (
                    <FeedDetailSection $padding="1rem 1.15rem 1.25rem">
                        <FeedSectionLabel>Discussion</FeedSectionLabel>
                        {loading && !comments.length && (
                            <FeedInlineState role="status">Loading comments…</FeedInlineState>
                        )}
                        {error && !comments.length && (
                            <FeedInlineState $error role="alert">
                                Comments could not be loaded.
                                <div>
                                    <FeedRetryButton type="button" onClick={() => loadComments(page)}>
                                        Retry
                                    </FeedRetryButton>
                                </div>
                            </FeedInlineState>
                        )}
                        {!loading && !error && !comments.length && (
                            <FeedInlineState>No comments yet. Start the conversation.</FeedInlineState>
                        )}
                        {comments.length > 0 && (
                            <FeedModalList aria-label="Comments">
                                {comments.map(comment => (
                                    <NoticeboardComment
                                        key={comment.uuid}
                                        comment={comment}
                                        token={token}
                                        interactionUuid={interactionUuid}
                                        showReplies
                                        onReply={setReplyingTo}
                                        compact={false}
                                    />
                                ))}
                            </FeedModalList>
                        )}
                        {nextPageUrl && (
                            <FeedTextButton
                                type="button"
                                disabled={loading}
                                onClick={() => loadComments(page + 1)}
                            >
                                {loading ? "Loading…" : "Load more comments"}
                            </FeedTextButton>
                        )}
                    </FeedDetailSection>
                )}
            </StandardModalScrollArea>
            {allowComments && (
                <FeedDetailComposerDock>
                    <FeedComposer onSubmit={handleSubmit}>
                        <FeedComposerInput
                            value={commentText}
                            onChange={event => setCommentText(event.target.value)}
                            placeholder={replyingTo ? getReplyPlaceholder(replyingTo) : "Write a comment…"}
                            aria-label={replyingTo ? getReplyPlaceholder(replyingTo) : "Write a comment"}
                        />
                        <FeedComposerButton type="submit" disabled={!commentText.trim() || submitting}>
                            {submitting ? "Posting…" : replyingTo ? "Reply" : "Post"}
                        </FeedComposerButton>
                        {replyingTo && (
                            <FeedTextButton type="button" onClick={() => setReplyingTo(null)}>
                                Cancel reply
                            </FeedTextButton>
                        )}
                    </FeedComposer>
                    {submitError && (
                        <FeedInlineState $compact $error role="alert">
                            Your comment could not be posted. Please try again.
                        </FeedInlineState>
                    )}
                </FeedDetailComposerDock>
            )}
        </StandardModalFixedLayout>
    );
};

export default NoticeboardCommentsPanel;
